<template>
  <Loader :value="promises.fetchOne" :message="$metaInfo.title" class="flatfinder-mutate">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumbs">
        <template #right>
          <div class="row wrap g-1">
            <Actions placement="flatfinder" :query="{ flatfinder: id }">
              <template #bottom>
                <ButtonComponent
                  v-if="!isNew"
                  :label="$t('delete')"
                  @click="removeDialog = true"
                  :icon="['fal', 'times']"
                />
              </template>
            </Actions>
            <ButtonComponent
              :label="$t('save')"
              :success-label="$t('saved')"
              :loading-label="$t('saving')"
              :icon="['fal', 'arrow-to-bottom']"
              theme="primary"
              :promise="promises.mutate"
              :class="{ 'elder-button--disabled': !validator.passes || !hasChanges }"
              v-tooltip="{ content: hasChanges ? null : $t('noUnsavedChanges') }"
              @click="submit"
            />
          </div>
        </template>
      </HeaderComponent>
      <StepperComponent :value="steps" ref="stepper" state-handler="route" @submit="submit">
        <template #after="{ meta, next, prev }">
          <div class="row g-1 center">
            <ButtonComponent
              v-if="meta.prev"
              :label="$t('prev')"
              type="submit"
              :icon="['fal', 'angle-left']"
              icon-placement="left"
              @click="onNavigation(prev)"
            />
            <ButtonComponent
              v-if="meta.next"
              :label="$t('next')"
              type="submit"
              :icon="['fal', 'angle-right']"
              @click="onNavigation(next)"
            />
            <ButtonComponent
              v-if="!meta.next"
              :label="$t('complete')"
              :success-label="$t('completed')"
              :loading-label="$t('saving')"
              :icon="['fal', 'arrow-to-right']"
              theme="success"
              type="button"
              :disabled="!validator.passes"
              :promise="promises.mutate"
              @click="complete"
            />
          </div>
        </template>
      </StepperComponent>
      <ModalComponent :show="showResidentialWarning" class="flatfinder-mutate__residential-warning">
        <Card theme="flat">
          <template #header>
            <FontAwesomeIcon :icon="['fal', 'exclamation-triangle']" size="3x" />
            <strong class="mt-1">{{ $t('flatfinderCreateResidentialWarningTitle') }}</strong>
          </template>
          <template #default>
            <p>{{ $t('flatfinderCreateResidentialWarning') }}</p>
            <p class="fade text--small">({{ $t('flatfinderCreateResidentialWarningException') }})</p>
          </template>
          <template #footer>
            <ButtonComponent
              @click="
                $router.push({
                  name: 'project-dashboard',
                  params: { id: $path('project.id', item) },
                })
              "
              :label="$t('cancel')"
              :icon="['fal', 'times']"
            />
            <ButtonComponent
              theme="primary"
              @click="ignoreResidentialWarning = true"
              :label="$t('continue')"
              :icon="['fal', 'angle-right']"
            />
          </template>
        </Card>
      </ModalComponent>
      <RemoveComponent
        :show.sync="removeDialog"
        :title="title"
        :action="() => remove({ id })"
        @onSuccess="
          $router.push({
            name: 'project-edit',
            params: { id: $path('project.id', item), step: 'flatfinders' },
          })
        "
        :resource="$tc('unitSelector')"
      />
    </div>
  </Loader>
</template>

<script>
import Validator from '@/mixins/validator-v2'
import { mapState, mapActions } from 'vuex'
import { mapi18n } from '@/i18n'
import ModelMixin from '@/mixins/Model'
import Model from '@/models/Flatfinder'

import HeaderComponent from '@/components/Header'
import Card from '@/components/Card'
import RemoveComponent from '@/components/Remove'
import { StepperComponent } from 'vue-elder-stepper'
import { InputComponent } from 'vue-elder-input'
import { ModalComponent } from 'vue-elder-modal'
import Steps from './Steps'
import Actions from '@/components/Actions'

export default {
  mixins: [
    Validator({
      rules: {
        name: 'required',
        image: 'required',
      },
      labels: 'labels',
      data: 'data',
    }),
    ModelMixin({
      model: Model,
      guard: true,
    }),
  ],
  props: {
    id: String,
    step: String,
    project: String,
  },
  data() {
    return {
      ignoreResidentialWarning: false,
      removeDialog: false,
    }
  },
  watch: {
    id: {
      handler(id = '') {
        return this.fetch([
          { id, project: this.project, isNew: this.isNew },
          {
            query: 'ProjectFlatfinder',
            transform: v => {
              let base = { project: v.Reference }

              if (!v.Flatfinder) return base

              return {
                ...base,
                ...v.Flatfinder,
                items: v.Flatfinder.items.map(item => {
                  let { reference, data } = item
                  let { onModel, ...ref } = reference
                  return {
                    data,
                    reference: {
                      onModel,
                      ref,
                    },
                  }
                }),
              }
            },
          },
        ])
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState('Flatfinder', ['item', 'promises', 'states']),
    ...mapi18n('labels', ['name', ['image', 1]]),
    showResidentialWarning() {
      if (this.ignoreResidentialWarning) return
      return !this.states.fetchOne && !this.$path('item.project.stats.count')
    },
    isNew() {
      return !this.id
    },
    title() {
      return this.item.name || this.$t('loading', { resource: this.$tc('unitSelector').toLowerCase() }) + '...'
    },
    breadcrumbs() {
      return [
        { title: this.$tc('project', 2), action: { name: 'projects' } },
        {
          title: this.$path('item.project.name'),
          action: {
            name: 'project-dashboard',
            params: { id: this.$path('item.project.id') },
          },
        },
        {
          title: this.$tc('unitSelector', 2),
          action: {
            name: 'project-edit',
            params: {
              id: this.$path('item.project.id'),
              step: 'flatfinders',
            },
          },
        },
        { title: this.$metaInfo.title },
      ]
    },
    steps() {
      let props = {
        value: this.data,
        labels: this.labels,
        validator: this.validator,
        promises: this.promises,
        project: this.project,
      }

      let listeners = {
        submit: () => this.submit(),
      }

      return Steps().map(raw => {
        const { icon, ...s } = raw
        s.props = {
          ...(s.props || {}),
          ...props,
        }
        s.listeners = {
          ...(s.listeners || {}),
          ...listeners,
        }
        s.disabled = s.id == 'builder' && this.isNew
        return s
      })
    },
  },
  methods: {
    ...mapActions('Flatfinder', ['fetch', 'mutate', 'remove']),
    submit() {
      if (!this.validator.passes || !this.hasChanges) return Promise.resolve()

      let data = this.getData()
      if (this.isNew) data.project = this.project
      if (data.parent) data.parent = data.parent.id

      data.items = data.items.map(item => {
        item.reference.ref = item.reference.ref.id
        return item
      })

      return this.mutate([
        data,
        {
          transform: v => {
            return {
              ...v[`Flatfinder${this.isNew ? 'Create' : 'Update'}`],
              items: v[`Flatfinder${this.isNew ? 'Create' : 'Update'}`].items.map(item => {
                let { reference, data } = item
                let { onModel, ...ref } = reference
                return {
                  data,
                  reference: {
                    onModel,
                    ref,
                  },
                }
              }),
            }
          },
        },
      ]).then(data => {
        if (!this.isNew) return
        if (this.$route.query.returnTo) return this.$router.push(this.$route.query.returnTo)
        this.$router.replace({
          name: 'project-flatfinder-edit',
          params: { id: data.id, project: this.project, step: this.step },
        })
      })
    },
    complete() {
      return this.submit().then(() =>
        this.$router.push({ name: 'project-edit', params: { id: this.project, step: 'flatfinders' } }),
      )
    },
    onNavigation(callback) {
      callback()
      this.submit()
    },
  },
  metaInfo() {
    return {
      title: this.isNew ? this.$t('createUnitSelector') : this.title,
    }
  },
  components: {
    HeaderComponent,
    RemoveComponent,
    ModalComponent,
    StepperComponent,
    Card,
    Actions,
  },
}
</script>

<style lang="scss">
.flatfinder-mutate {
  background-color: $background-grey;

  .elder-stepper__component > * {
    margin: 2rem auto;
  }

  .elder-modal {
    z-index: get-layer('modal');
  }

  &__residential-warning {
    .card__header {
      background-color: css-alpha('warning', 0.2);
      color: var(--warning);
      min-height: 170px;
    }

    .card__content {
      text-align: center;
      max-width: 400px;
    }

    .card__footer-content {
      justify-content: center;
    }
  }
}
</style>
